import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import toast, { Toaster } from 'react-hot-toast';
import DynamicListInput from '../components/ui/DynamicListInput';
import ChipTag from '../components/ui/ChipTag';
import FormatContentEditor from '../components/FormatContentEditor';
import ProductPackEditor from '../components/ProductPackEditorV4';
import { txt } from '../utils/bilingualText';
import {
  createPackV3,
  updatePackV3,
  getPackV3,
  PACK_TYPES,
  PACK_STATUS,
  VISIBILITY,
} from '../api/knowledgePacksV3';
import { EMZI_COLORS, EMZI_SHADOWS, EMZI_RADIUS, EMZI_SPACING, getHeaderFlat } from '../styles/emziCI';
import { secureStorage } from '../utils/secureStorage';
import { BACKEND_URL } from '../utils/backendUrl';

/**
 * Knowledge Pack Wizard V3
 * ========================
 * Multi-step wizard for creating and editing V3 Knowledge Packs
 * 
 * Steps:
 * 1. Basic Info (name, description, type, visibility)
 * 2. Type-Specific Content (dynamic based on pack_type)
 * 3. Relationships (link to other packs)
 * 4. Compliance & AI Settings
 * 5. Review & Submit
 */
const KnowledgePackWizardV3 = () => {
  const navigate = useNavigate();
  const { packId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const t = useLang();
  
  // Get type from URL parameter (from dashboard click)
  const preSelectedType = searchParams.get('type');

  // Wizard State
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Validation State
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  
  // Auto-Save State
  const [draftId, setDraftId] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Form Data
  const [formData, setFormData] = useState({
    // Basic Info
    pack_type: preSelectedType || 'product',
    name: { en: '', ms: '' },
    description: { en: '', ms: '' },
    visibility: 'public',  // Changed from 'global' to match V3 enum
    allowed_studios: [],
    allowed_groups: [],
    tags: [],

    // Type-Specific Content (will be dynamically filled)
    content: {},

    // Relationships (V3 schema-compliant)
    relationships: {
      parent_pack_id: null,
      child_pack_ids: [],
      linked_packs: [],
      values_pack_id: '',  // Required
      compliance_pack_id: '',  // Required
    },

    // Compliance
    compliance_profile: {
      level: 'medium',  // Changed from 'standard' to match V3 enum
      restricted_phrases: [],
      safe_alternatives: {},  // Changed from array to dict
      substitution_rules: [],
      safe_examples: [],
      warnings: [],
    },

    // AI Context (V3 schema-compliant)
    ai_context: {
      base_weight: 0.5,
      recommended_combinations: [],
      conflict_warning: [],
      tone_influence: {
        formal: 0.0,
        casual: 0.0,
        spiritual: 0.0,
        promotional: 0.0,
        educational: 0.0,
      },
      soa_stage: [],
    },

    // Version metadata
    version: '1.0.0',

    // Metadata
    status: 'draft',
  });

  // RBAC
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management' || user?.role === 'management_functional';

  // Load pack for editing OR restore draft
  useEffect(() => {
    if (packId) {
      setIsEditMode(true);
      loadPack();
    } else {
      // Try to restore draft
      restoreDraft();
    }
  }, [packId]);

  const loadPack = async () => {
    try {
      setLoading(true);
      const response = await getPackV3(packId);
      
      if (response.success && response.pack) {
        // Map V3 pack to form data
        const pack = response.pack;
        setFormData({
          pack_type: pack.pack_type,
          name: pack.name,
          description: pack.description,
          visibility: pack.metadata?.visibility || 'global',
          allowed_studios: pack.metadata?.allowed_studios || [],
          allowed_groups: pack.metadata?.allowed_groups || [],
          tags: pack.metadata?.tags || [],
          content: pack.content || {},
          relationships: pack.relationships || {
            parent_packs: [],
            child_packs: [],
            linked_packs: [],
          },
          compliance_profile: pack.compliance_profile || {
            level: 'standard',
            restricted_phrases: [],
            safe_alternatives: [],
          },
          ai_context: pack.ai_context || {
            tone_influence: 'neutral',
            context_weight: 5,
            soa_stages: ['opening', 'discovery', 'presentation', 'closing'],
          },
          status: pack.metadata?.status || 'draft',
        });
      }
    } catch (err) {
      console.error('Failed to load pack:', err);
      setError(err.message || 'Failed to load pack');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // VALIDATION FUNCTIONS
  // ============================================================
  
  const validateStep1 = () => {
    const errors = {};
    
    // Required fields for all types
    if (!formData.name.en?.trim()) errors.name_en = 'English name is required';
    if (!formData.name.ms?.trim()) errors.name_ms = 'Malay name is required';
    if (!formData.description.en?.trim() && !formData.description.ms?.trim()) {
      errors.description = 'At least one description (EN or MS) is required';
    }
    if (!formData.visibility) errors.visibility = 'Visibility is required';
    
    return errors;
  };
  
  const validateStep2 = () => {
    const errors = {};
    const { pack_type, content } = formData;
    
    // Type-specific validation
    switch (pack_type) {
      case 'product':
        if (!content.features?.trim()) errors.features = 'Product features are required';
        if (!content.benefits?.trim()) errors.benefits = 'Product benefits are required';
        break;
      case 'promotion':
        if (!content.start_date) errors.start_date = 'Start date is required';
        if (!content.end_date) errors.end_date = 'End date is required';
        if (!content.offer) errors.offer = 'Offer summary is required';
        break;
      case 'direction':
        if (!content.strategic_objective?.trim()) errors.strategic_objective = 'Strategic objective is required';
        if (!content.key_messages?.trim()) errors.key_messages = 'At least one key message is required';
        break;
      case 'compliance':
        if (!content.regulations?.trim()) errors.regulations = 'Regulations are required';
        if (!content.prohibited_terms?.trim()) errors.prohibited_terms = 'At least one prohibited term is required';
        break;
    }
    
    return errors;
  };
  
  const validateStep3 = () => {
    const errors = {};
    
    // Relationships are optional, but if provided, must be valid
    // We'll validate this when the pack picker is implemented
    
    return errors;
  };
  
  const validateCurrentStep = () => {
    let errors = {};
    
    switch (currentStep) {
      case 1:
        errors = validateStep1();
        break;
      case 2:
        errors = validateStep2();
        break;
      case 3:
        errors = validateStep3();
        break;
      default:
        errors = {};
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  // ============================================================
  // AUTO-SAVE FUNCTIONS
  // ============================================================
  
  const saveDraft = async () => {
    try {
      setIsSaving(true);
      
      const tempId = draftId || `draft_${user?.id || 'user'}_${Date.now()}`;
      
      const draftPayload = {
        ...formData,
        status: 'draft',
        temp_id: tempId,
        user_id: user?.id,
        saved_at: new Date().toISOString(),
      };
      
      const response = await fetch(`${BACKEND_URL}/api/v3/registry/drafts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
        },
        body: JSON.stringify(draftPayload),
      });
      
      if (response.ok) {
        const result = await response.json();
        const newDraftId = result.draft_id || tempId;
        setDraftId(newDraftId);
        setLastSaved(new Date());
        
        // Store draft ID in localStorage for restoration
        localStorage.setItem('knowledge_pack_draft_id', newDraftId);
        
        // Show subtle toast notification
        toast.success('Draft saved', {
          duration: 2000,
          position: 'bottom-center',
          style: {
            fontSize: '12px',
            padding: '8px 12px',
          },
        });
      }
    } catch (err) {
      console.error('Failed to save draft:', err);
      toast.error('Failed to save draft', {
        duration: 2000,
        position: 'bottom-center',
      });
    } finally {
      setIsSaving(false);
    }
  };
  
  const restoreDraft = async () => {
    try {
      const savedDraftId = localStorage.getItem('knowledge_pack_draft_id');
      if (!savedDraftId) return;
      
      const response = await fetch(
        `${BACKEND_URL}/api/v3/registry/drafts/${savedDraftId}`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
          },
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        if (result.draft) {
          setFormData(result.draft);
          setDraftId(savedDraftId);
          setLastSaved(new Date(result.draft.saved_at));
          
          toast.success('Draft restored', {
            duration: 3000,
            position: 'top-center',
          });
        }
      }
    } catch (err) {
      console.error('Failed to restore draft:', err);
    }
  };
  
  const deleteDraft = async () => {
    try {
      if (!draftId) return;
      
      await fetch(
        `${BACKEND_URL}/api/v3/registry/drafts/${draftId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
          },
        }
      );
      
      localStorage.removeItem('knowledge_pack_draft_id');
      setDraftId(null);
      
      toast.success('Draft deleted', {
        duration: 2000,
        position: 'bottom-center',
      });
    } catch (err) {
      console.error('Failed to delete draft:', err);
    }
  };
  
  // Auto-save every 8 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isEditMode && formData.name.en) {
        saveDraft();
      }
    }, 8000);
    
    return () => clearInterval(interval);
  }, [formData, isEditMode]);
  
  // Save on step change
  useEffect(() => {
    if (currentStep > 1 && !isEditMode) {
      saveDraft();
    }
  }, [currentStep]);

  // Handle form data change
  const updateFormData = (path, value) => {
    setFormData((prev) => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        if (!current[keys[i]]) current[keys[i]] = {};
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };
  
  // Mark field as touched
  const handleBlur = (fieldName) => {
    setTouched(prev => ({ ...prev, [fieldName]: true }));
  };
  
  // Telemetry logging (prepare for future analytics)
  const logTelemetry = (eventName, metadata = {}) => {
    console.log('[TELEMETRY]', eventName, {
      ...metadata,
      timestamp: new Date().toISOString(),
      user_id: user?.id,
    });
    // Future: Send to analytics backend
  };

  // Navigation
  const nextStep = () => {
    // Validate current step before proceeding
    if (!validateCurrentStep()) {
      // Mark all fields as touched to show errors
      setTouched(prev => ({
        ...prev,
        ...Object.keys(validationErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {})
      }));
      
      // Log telemetry
      logTelemetry('validate_fail', {
        step: currentStep,
        errors: Object.keys(validationErrors),
      });
      
      // Show error toast
      toast.error('Please fix the errors before proceeding', {
        duration: 3000,
        position: 'top-center',
      });
      
      return;
    }
    
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
      
      // Log telemetry
      logTelemetry('step_complete', {
        step: currentStep,
      });
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Submit
  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build V3 pack payload
      const payload = {
        pack_type: formData.pack_type,
        version: formData.version || '1.0.0',
        name: formData.name,
        description: formData.description,
        content: formData.content,
        relationships: formData.relationships,
        compliance_profile: formData.compliance_profile,
        ai_context: formData.ai_context,
        metadata: {
          status: formData.status,
          visibility: formData.visibility,
          allowed_studios: formData.allowed_studios,
          allowed_groups: formData.allowed_groups,
          tags: formData.tags,
        },
      };

      let response;
      if (isEditMode) {
        response = await updatePackV3(packId, payload);
      } else {
        response = await createPackV3(payload);
      }

      if (response.success) {
        // Delete draft after successful submission
        if (!isEditMode && draftId) {
          await deleteDraft();
        }
        
        toast.success(
          isEditMode ? 'Pack updated successfully!' : 'Pack created successfully!',
          {
            duration: 3000,
            position: 'top-center',
          }
        );
        
        // Log telemetry event
        logTelemetry('submit_success', {
          pack_type: formData.pack_type,
          is_edit: isEditMode,
        });
        
        setTimeout(() => navigate('/knowledge-packs'), 1000);
      } else {
        throw new Error(response.message || 'Failed to save pack');
      }
    } catch (err) {
      console.error('Failed to save pack:', err);
      setError(err.message || 'Failed to save pack');
    } finally {
      setLoading(false);
    }
  };

  // Log wizard open on mount
  useEffect(() => {
    logTelemetry('wizard_open', {
      is_edit: isEditMode,
      pack_type: formData.pack_type,
    });
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" style={{ height: '100dvh' }}>
      {/* Toast Notifications */}
      <Toaster />
      
      {/* Header */}
      <div style={{ ...getHeaderFlat(), padding: '12px 16px' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div
              className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-xl"
              style={{ boxShadow: EMZI_SHADOWS.sm }}
            >
              📝
            </div>
            <div>
              <h1 className="text-lg font-black leading-tight">
                {isEditMode ? 'Edit Pack' : 'Create Knowledge Pack'}
              </h1>
              <div className="flex items-center gap-2">
                <p className="text-xs font-medium opacity-90">V3 Wizard</p>
                {!isEditMode && lastSaved && (
                  <span className="text-xs opacity-75">
                    • Draft saved {new Date(lastSaved).toLocaleTimeString()}
                  </span>
                )}
                {isSaving && (
                  <span className="text-xs opacity-75">
                    • Saving...
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => navigate('/knowledge-packs')}
            className="text-xs font-semibold px-3 py-1 bg-white/10 hover:bg-white/20 rounded transition"
          >
            ✕ Cancel
          </button>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          {[1, 2, 3, 4, 5].map((step) => (
            <div
              key={step}
              className={`flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold transition ${
                step === currentStep
                  ? 'bg-blue-600 text-white'
                  : step < currentStep
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step < currentStep ? '✓' : step}
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] font-medium text-gray-600">
          <span>Basic Info</span>
          <span>Content</span>
          <span>Relations</span>
          <span>Settings</span>
          <span>Review</span>
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4 pb-24 overflow-y-auto" style={{ maxHeight: 'calc(100dvh - 200px)' }}>
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <p className="text-sm font-semibold text-red-600">⚠️ {error}</p>
          </div>
        )}

        {currentStep === 1 && (
          <BasicInfoStep 
            formData={formData} 
            updateFormData={updateFormData}
            validationErrors={validationErrors}
            touched={touched}
            handleBlur={handleBlur}
          />
        )}
        {currentStep === 2 && (
          <ContentStep 
            formData={formData} 
            updateFormData={updateFormData}
            validationErrors={validationErrors}
            touched={touched}
            handleBlur={handleBlur}
          />
        )}
        {currentStep === 3 && (
          <RelationshipsStep formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 4 && (
          <SettingsStep formData={formData} updateFormData={updateFormData} />
        )}
        {currentStep === 5 && <ReviewStep formData={formData} />}
      </div>

      {/* Navigation Buttons */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-lg"
        style={{ maxWidth: '428px', margin: '0 auto' }}
      >
        <div className="flex gap-2">
          {currentStep > 1 && (
            <button
              onClick={prevStep}
              className="flex-1 py-3 text-sm font-semibold rounded-lg transition"
              style={{
                backgroundColor: EMZI_COLORS.surface,
                color: EMZI_COLORS.textPrimary,
              }}
            >
              ← Previous
            </button>
          )}
          {currentStep < 5 ? (
            <button
              onClick={nextStep}
              className="flex-1 py-3 text-white text-sm font-bold rounded-lg shadow-md transition"
              style={{ backgroundColor: EMZI_COLORS.primary }}
            >
              Next →
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 py-3 text-white text-sm font-bold rounded-lg shadow-md transition"
              style={{
                backgroundColor: loading ? EMZI_COLORS.textMuted : EMZI_COLORS.success,
              }}
            >
              {loading ? '⏳ Saving...' : isEditMode ? '✓ Update Pack' : '✓ Create Pack'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STEP 1: BASIC INFO
// ============================================================================
const BasicInfoStep = ({ formData, updateFormData, validationErrors = {}, touched = {}, handleBlur }) => {
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4" style={{ borderRadius: EMZI_RADIUS.md }}>
        <h3 className="text-base font-bold text-gray-900 mb-3">Basic Information</h3>

        {/* Pack Type */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Pack Type *</label>
          <select
            value={formData.pack_type}
            onChange={(e) => updateFormData('pack_type', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            {Object.values(PACK_TYPES).map((type) => (
              <option key={type} value={type}>
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Name (English) */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Name (English) *</label>
          <input
            type="text"
            value={formData.name.en}
            onChange={(e) => updateFormData('name.en', e.target.value)}
            onBlur={() => handleBlur('name_en')}
            placeholder="e.g., Premium Olive Oil"
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
              validationErrors.name_en && touched.name_en 
                ? 'border-red-500 bg-red-50' 
                : 'border-gray-300'
            }`}
          />
          {validationErrors.name_en && touched.name_en && (
            <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.name_en}</p>
          )}
        </div>

        {/* Name (Malay) */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Name (Malay) *</label>
          <input
            type="text"
            value={formData.name.ms}
            onChange={(e) => updateFormData('name.ms', e.target.value)}
            onBlur={() => handleBlur('name_ms')}
            placeholder="e.g., Minyak Zaitun Premium"
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
              validationErrors.name_ms && touched.name_ms 
                ? 'border-red-500 bg-red-50' 
                : 'border-gray-300'
            }`}
          />
          {validationErrors.name_ms && touched.name_ms && (
            <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.name_ms}</p>
          )}
        </div>

        {/* Description (English) */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Description (English) *
          </label>
          <textarea
            value={formData.description.en}
            onChange={(e) => updateFormData('description.en', e.target.value)}
            onBlur={() => handleBlur('description')}
            placeholder="Brief description of this pack..."
            rows={3}
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
              validationErrors.description && touched.description 
                ? 'border-red-500 bg-red-50' 
                : 'border-gray-300'
            }`}
          />
          {validationErrors.description && touched.description && (
            <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.description}</p>
          )}
        </div>

        {/* Description (Malay) */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Description (Malay)
          </label>
          <textarea
            value={formData.description.ms}
            onChange={(e) => updateFormData('description.ms', e.target.value)}
            placeholder="Penerangan ringkas tentang pek ini..."
            rows={3}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Visibility */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Visibility *</label>
          <select
            value={formData.visibility}
            onChange={(e) => updateFormData('visibility', e.target.value)}
            onBlur={() => handleBlur('visibility')}
            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
              validationErrors.visibility && touched.visibility 
                ? 'border-red-500 bg-red-50' 
                : 'border-gray-300'
            }`}
          >
            <option value="public">Public (All Users)</option>
            <option value="group_scoped">Group Scoped</option>
            <option value="admin_only">Admin Only</option>
          </select>
          {validationErrors.visibility && touched.visibility && (
            <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.visibility}</p>
          )}
        </div>

        {/* Tags - ChipTag UI */}
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Tags</label>
          <div className="flex flex-wrap gap-2 mb-2">
            {formData.tags.map((tag, idx) => (
              <ChipTag
                key={`item-${idx}`}
                label={tag}
                size="sm"
                onDelete={() => {
                  const updated = formData.tags.filter((_, i) => i !== idx);
                  updateFormData('tags', updated);
                }}
              />
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              id="new-tag-input"
              placeholder="Type tag and press Enter..."
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const newTag = e.target.value.trim();
                  if (newTag && !formData.tags.includes(newTag)) {
                    updateFormData('tags', [...formData.tags, newTag]);
                    e.target.value = '';
                  }
                }
              }}
            />
            <button
              type="button"
              onClick={() => {
                const input = document.getElementById('new-tag-input');
                const newTag = input.value.trim();
                if (newTag && !formData.tags.includes(newTag)) {
                  updateFormData('tags', [...formData.tags, newTag]);
                  input.value = '';
                }
              }}
              style={{
                padding: '8px 16px',
                backgroundColor: EMZI_COLORS.accent,
                color: EMZI_COLORS.textWhite,
                border: 'none',
                borderRadius: EMZI_RADIUS.sm,
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              + Add
            </button>
          </div>
          <p className="text-[10px] text-gray-500 mt-1">Press Enter or click "+ Add" to add tags</p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STEP 2: TYPE-SPECIFIC CONTENT
// ============================================================================
const ContentStep = ({ formData, updateFormData, validationErrors = {}, touched = {}, handleBlur }) => {
  const packType = formData.pack_type;
  
  // Debug logging
  console.log('[ContentStep] packType:', packType);
  console.log('[ContentStep] content:', formData.content);
  console.log('[ContentStep] Is format?:', packType === 'format');

  // Helper to update content fields
  const updateContent = (field, value) => {
    updateFormData(`content.${field}`, value);
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4" style={{ borderRadius: EMZI_RADIUS.md }}>
        <h3 className="text-base font-bold text-gray-900 mb-3">
          {packType.charAt(0).toUpperCase() + packType.slice(1)} Content
        </h3>

        {packType === 'product' && (
          <ProductPackEditor
            content={formData.content} 
            updateContent={updateContent}
            packId={packId}
            userId={user?.user_id || ''}
            validationErrors={validationErrors}
            touched={touched}
            handleBlur={handleBlur}
          />
        )}
        {packType === 'promotion' && (
          <PromotionContent 
            content={formData.content} 
            updateContent={updateContent}
            validationErrors={validationErrors}
            touched={touched}
            handleBlur={handleBlur}
          />
        )}
        {packType === 'direction' && (
          <DirectionContent 
            content={formData.content} 
            updateContent={updateContent}
            validationErrors={validationErrors}
            touched={touched}
            handleBlur={handleBlur}
          />
        )}
        {packType === 'values' && (
          <ValuesContent 
            content={formData.content} 
            updateContent={updateContent}
            validationErrors={validationErrors}
            touched={touched}
            handleBlur={handleBlur}
          />
        )}
        {packType === 'services' && (
          <ServicesContent 
            content={formData.content} 
            updateContent={updateContent}
            validationErrors={validationErrors}
            touched={touched}
            handleBlur={handleBlur}
          />
        )}
        {packType === 'compliance' && (
          <ComplianceContent 
            content={formData.content} 
            updateContent={updateContent}
            validationErrors={validationErrors}
            touched={touched}
            handleBlur={handleBlur}
          />
        )}
        {packType === 'viral' && (
          <ViralContent 
            content={formData.content} 
            updateContent={updateContent}
            validationErrors={validationErrors}
            touched={touched}
            handleBlur={handleBlur}
          />
        )}
        {packType === 'trend' && (
          <TrendContent 
            content={formData.content} 
            updateContent={updateContent}
            validationErrors={validationErrors}
            touched={touched}
            handleBlur={handleBlur}
          />
        )}
        {packType === 'format' && (
          <FormatContentEditor
            content={formData.content}
            updateContent={updateContent}
          />
        )}
      </div>
    </div>
  );
};

// Product Content Fields - LEGACY (now using ProductPackEditor component)
// Keeping for reference
/*
const ProductContent = ({ content, updateContent, validationErrors = {}, touched = {}, handleBlur }) => {
  // Convert features string to array for DynamicListInput
  const featuresArray = content.features ? content.features.split('\n').filter(f => f.trim()) : [];
  const benefitsArray = content.benefits ? content.benefits.split('\n').filter(b => b.trim()) : [];
  
  return (
    <>
      <DynamicListInput
        items={featuresArray}
        onChange={(items) => updateContent('features', items.join('\n'))}
        placeholder="Enter a feature..."
        label="Product Features *"
        type="text"
        maxItems={10}
      />
      {validationErrors.features && touched.features && (
        <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.features}</p>
      )}
      
      <DynamicListInput
        items={benefitsArray}
        onChange={(items) => updateContent('benefits', items.join('\n'))}
        placeholder="Enter a benefit..."
        label="Benefits *"
        type="text"
        maxItems={10}
      />
      {validationErrors.benefits && touched.benefits && (
        <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.benefits}</p>
      )}
      
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-700 mb-1">Price Range</label>
        <input
          type="text"
          value={content.price_range || ''}
          onChange={(e) => updateContent('price_range', e.target.value)}
          placeholder="e.g., RM 50 - RM 100"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Target Audience</label>
        <input
          type="text"
          value={content.target_audience || ''}
          onChange={(e) => updateContent('target_audience', e.target.value)}
          placeholder="e.g., Health-conscious professionals"
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>
    </>
  );
};
*/

// Promotion Content Fields
const PromotionContent = ({ content, updateContent, validationErrors = {}, touched = {}, handleBlur }) => (
  <>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Campaign Name</label>
      <input
        type="text"
        value={content.campaign_name || ''}
        onChange={(e) => updateContent('campaign_name', e.target.value)}
        placeholder="e.g., Summer Sale 2025"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Discount/Offer *</label>
      <input
        type="text"
        value={content.offer || ''}
        onChange={(e) => updateContent('offer', e.target.value)}
        onBlur={() => handleBlur('offer')}
        placeholder="e.g., 20% OFF + Free Shipping"
        className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
          validationErrors.offer && touched.offer 
            ? 'border-red-500 bg-red-50' 
            : 'border-gray-300'
        }`}
      />
      {validationErrors.offer && touched.offer && (
        <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.offer}</p>
      )}
    </div>
    <div className="grid grid-cols-2 gap-2 mb-4">
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">Start Date *</label>
        <input
          type="date"
          value={content.start_date || ''}
          onChange={(e) => updateContent('start_date', e.target.value)}
          onBlur={() => handleBlur('start_date')}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
            validationErrors.start_date && touched.start_date 
              ? 'border-red-500 bg-red-50' 
              : 'border-gray-300'
          }`}
        />
        {validationErrors.start_date && touched.start_date && (
          <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.start_date}</p>
        )}
      </div>
      <div>
        <label className="block text-xs font-semibold text-gray-700 mb-1">End Date *</label>
        <input
          type="date"
          value={content.end_date || ''}
          onChange={(e) => updateContent('end_date', e.target.value)}
          onBlur={() => handleBlur('end_date')}
          className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
            validationErrors.end_date && touched.end_date 
              ? 'border-red-500 bg-red-50' 
              : 'border-gray-300'
          }`}
        />
        {validationErrors.end_date && touched.end_date && (
          <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.end_date}</p>
        )}
      </div>
    </div>
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">Terms & Conditions</label>
      <textarea
        value={content.terms || ''}
        onChange={(e) => updateContent('terms', e.target.value)}
        placeholder="Promotion terms..."
        rows={3}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
  </>
);

// Direction Content Fields
const DirectionContent = ({ content, updateContent, validationErrors = {}, touched = {}, handleBlur }) => (
  <>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Strategic Objective *</label>
      <textarea
        value={content.strategic_objective || ''}
        onChange={(e) => updateContent('strategic_objective', e.target.value)}
        onBlur={() => handleBlur('strategic_objective')}
        placeholder="Main strategic objective of this campaign direction..."
        rows={3}
        className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${
          validationErrors.strategic_objective && touched.strategic_objective 
            ? 'border-red-500 bg-red-50' 
            : 'border-gray-300'
        }`}
      />
      {validationErrors.strategic_objective && touched.strategic_objective && (
        <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.strategic_objective}</p>
      )}
    </div>
    <DynamicListInput
      items={content.key_messages ? content.key_messages.split('\n').filter(m => m.trim()) : []}
      onChange={(items) => updateContent('key_messages', items.join('\n'))}
      placeholder="Enter a key message..."
      label="Key Messages *"
      type="text"
      maxItems={8}
    />
    {validationErrors.key_messages && touched.key_messages && (
      <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.key_messages}</p>
    )}
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Audience Focus</label>
      <input
        type="text"
        value={content.audience_focus || ''}
        onChange={(e) => updateContent('audience_focus', e.target.value)}
        placeholder="e.g., Health-conscious millennials"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">Tone Guidelines</label>
      <input
        type="text"
        value={content.tone_guidelines || ''}
        onChange={(e) => updateContent('tone_guidelines', e.target.value)}
        placeholder="e.g., Warm, trustworthy, informative"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
  </>
);

// Values Content Fields
const ValuesContent = ({ content, updateContent, validationErrors = {}, touched = {}, handleBlur }) => {
  const coreValuesArray = content.core_values ? content.core_values.split('\n').filter(v => v.trim()) : [];
  const examplesArray = content.examples ? content.examples.split('\n').filter(e => e.trim()) : [];
  
  return (
    <>
      <DynamicListInput
        items={coreValuesArray}
        onChange={(items) => updateContent('core_values', items.join('\n'))}
        placeholder="Enter a core value..."
        label="Core Values"
        type="text"
        maxItems={10}
      />
      
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-700 mb-1">Value Descriptions</label>
        <textarea
          value={content.descriptions || ''}
          onChange={(e) => updateContent('descriptions', e.target.value)}
          placeholder="Description of each value..."
          rows={4}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        />
      </div>
      
      <DynamicListInput
        items={examplesArray}
        onChange={(items) => updateContent('examples', items.join('\n'))}
        placeholder="Enter an application example..."
        label="Application Examples"
        type="text"
        maxItems={8}
      />
    </>
  );
};

// Services Content Fields
const ServicesContent = ({ content, updateContent, validationErrors = {}, touched = {}, handleBlur }) => (
  <>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Service Name</label>
      <input
        type="text"
        value={content.service_name || ''}
        onChange={(e) => updateContent('service_name', e.target.value)}
        placeholder="e.g., Premium Consultation Service"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Service Description</label>
      <textarea
        value={content.description || ''}
        onChange={(e) => updateContent('description', e.target.value)}
        placeholder="Detailed service description..."
        rows={4}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">What's Included</label>
      <textarea
        value={content.inclusions || ''}
        onChange={(e) => updateContent('inclusions', e.target.value)}
        placeholder="What's included in this service (one per line)"
        rows={4}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">Pricing</label>
      <input
        type="text"
        value={content.pricing || ''}
        onChange={(e) => updateContent('pricing', e.target.value)}
        placeholder="e.g., RM 500/month"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
  </>
);

// Compliance Content Fields
const ComplianceContent = ({ content, updateContent, validationErrors = {}, touched = {}, handleBlur }) => {
  const regulationsArray = content.regulations ? content.regulations.split('\n').filter(r => r.trim()) : [];
  const prohibitedTermsArray = content.prohibited_terms ? content.prohibited_terms.split(',').map(t => t.trim()).filter(t => t) : [];
  const safeAlternativesArray = content.safe_alternatives ? content.safe_alternatives.split('\n').filter(s => s.trim()) : [];
  
  return (
    <>
      <DynamicListInput
        items={regulationsArray}
        onChange={(items) => updateContent('regulations', items.join('\n'))}
        placeholder="Enter a regulation or rule..."
        label="Regulations & Rules *"
        type="text"
        maxItems={15}
      />
      {validationErrors.regulations && touched.regulations && (
        <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.regulations}</p>
      )}
      
      <div className="mb-4">
        <label className="block text-xs font-semibold text-gray-700 mb-1">Prohibited Terms *</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {prohibitedTermsArray.map((term, idx) => (
            <ChipTag
              key={`item-${idx}`}
              label={term}
              size="sm"
              color={EMZI_COLORS.error}
              onDelete={() => {
                const updated = prohibitedTermsArray.filter((_, i) => i !== idx);
                updateContent('prohibited_terms', updated.join(', '));
              }}
            />
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            id="new-prohibited-term"
            placeholder="Type term and press Enter..."
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const newTerm = e.target.value.trim();
                if (newTerm && !prohibitedTermsArray.includes(newTerm)) {
                  updateContent('prohibited_terms', [...prohibitedTermsArray, newTerm].join(', '));
                  e.target.value = '';
                }
              }
            }}
          />
          <button
            type="button"
            onClick={() => {
              const input = document.getElementById('new-prohibited-term');
              const newTerm = input.value.trim();
              if (newTerm && !prohibitedTermsArray.includes(newTerm)) {
                updateContent('prohibited_terms', [...prohibitedTermsArray, newTerm].join(', '));
                input.value = '';
              }
            }}
            style={{
              padding: '8px 16px',
              backgroundColor: EMZI_COLORS.accent,
              color: EMZI_COLORS.textWhite,
              border: 'none',
              borderRadius: EMZI_RADIUS.sm,
              fontSize: '12px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            + Add
          </button>
        </div>
        {validationErrors.prohibited_terms && touched.prohibited_terms && (
          <p className="text-xs text-red-500 mt-1">⚠️ {validationErrors.prohibited_terms}</p>
        )}
      </div>
      
      <DynamicListInput
        items={safeAlternativesArray}
        onChange={(items) => updateContent('safe_alternatives', items.join('\n'))}
        placeholder="Enter a safe alternative term..."
        label="Safe Alternatives"
        type="text"
        maxItems={15}
      />
    </>
  );
};

// Viral Content Fields
const ViralContent = ({ content, updateContent, validationErrors = {}, touched = {}, handleBlur }) => (
  <>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Viral Theme/Concept</label>
      <input
        type="text"
        value={content.theme || ''}
        onChange={(e) => updateContent('theme', e.target.value)}
        placeholder="e.g., Challenge, Transformation, Story"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Hook/Opening</label>
      <textarea
        value={content.hook || ''}
        onChange={(e) => updateContent('hook', e.target.value)}
        placeholder="Attention-grabbing opening..."
        rows={3}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Engagement Tactics</label>
      <textarea
        value={content.engagement_tactics || ''}
        onChange={(e) => updateContent('engagement_tactics', e.target.value)}
        placeholder="How to encourage shares, comments, likes..."
        rows={3}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">Call-to-Action</label>
      <input
        type="text"
        value={content.cta || ''}
        onChange={(e) => updateContent('cta', e.target.value)}
        placeholder="e.g., Try it yourself! Share your results!"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
  </>
);

// Trend Content Fields
const TrendContent = ({ content, updateContent, validationErrors = {}, touched = {}, handleBlur }) => (
  <>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Trend Name/Topic</label>
      <input
        type="text"
        value={content.trend_name || ''}
        onChange={(e) => updateContent('trend_name', e.target.value)}
        placeholder="e.g., Plant-Based Nutrition 2025"
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Current Market Context</label>
      <textarea
        value={content.market_context || ''}
        onChange={(e) => updateContent('market_context', e.target.value)}
        placeholder="What's happening in the market..."
        rows={4}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div className="mb-4">
      <label className="block text-xs font-semibold text-gray-700 mb-1">Consumer Behavior Insights</label>
      <textarea
        value={content.insights || ''}
        onChange={(e) => updateContent('insights', e.target.value)}
        placeholder="How consumers are responding..."
        rows={3}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-gray-700 mb-1">Recommended Messaging</label>
      <textarea
        value={content.messaging || ''}
        onChange={(e) => updateContent('messaging', e.target.value)}
        placeholder="How to position products in this trend..."
        rows={3}
        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
      />
    </div>
  </>
);

// ============================================================================
// STEP 3: RELATIONSHIPS
// ============================================================================
const RelationshipsStep = ({ formData, updateFormData }) => {
  const [searchResults, setSearchResults] = React.useState([]);
  const [isSearching, setIsSearching] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [relationshipType, setRelationshipType] = React.useState('requires');
  const [showPicker, setShowPicker] = React.useState(false);

  // Search packs
  const searchPacks = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setIsSearching(true);
      const response = await fetch(
        `${BACKEND_URL}/api/v3/packs?search=${encodeURIComponent(query)}&limit=10`,
        {
          headers: {
            'Authorization': `Bearer ${secureStorage.getItem('nexus_access_token')}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.packs || []);
      }
    } catch (err) {
      console.error('Failed to search packs:', err);
      toast.error('Failed to search packs');
    } finally {
      setIsSearching(false);
    }
  };

  // Add relationship
  const addRelationship = (pack) => {
    const newRelationship = {
      pack_id: pack.pack_id,
      pack_name: pack.name,
      pack_type: pack.pack_type,
      relationship_type: relationshipType,
      linked_by: 'user',
      linked_at: new Date().toISOString(),
    };

    const existingLinks = formData.relationships?.linked_packs || [];
    const isDuplicate = existingLinks.some(r => r.pack_id === pack.pack_id);

    if (!isDuplicate) {
      updateFormData('relationships.linked_packs', [...existingLinks, newRelationship]);
      toast.success(`Added ${txt(pack.name)} relationship`);
    } else {
      toast.error('This pack is already linked');
    }

    setSearchQuery('');
    setSearchResults([]);
    setShowPicker(false);
  };

  // Remove relationship
  const removeRelationship = (packId) => {
    const existingLinks = formData.relationships?.linked_packs || [];
    updateFormData(
      'relationships.linked_packs',
      existingLinks.filter(r => r.pack_id !== packId)
    );
    toast.success('Relationship removed');
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4" style={{ borderRadius: EMZI_RADIUS.md }}>
        <h3 className="text-base font-bold text-gray-900 mb-3">Pack Relationships</h3>
        <p className="text-xs text-gray-600 mb-4">
          Define how this pack relates to other packs. This helps the AI understand context and dependencies.
        </p>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
          <p className="text-xs font-semibold text-blue-700">
            💡 Tip: Use relationships to build knowledge hierarchies and ensure consistent messaging.
          </p>
        </div>

        {/* Relationship Type Selector */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Relationship Type</label>
          <select
            value={relationshipType}
            onChange={(e) => setRelationshipType(e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="requires">Requires (Dependency)</option>
            <option value="recommends">Recommends (Suggested)</option>
            <option value="conflicts_with">Conflicts With (Exclusion)</option>
            <option value="inherits_from">Inherits From (Parent)</option>
            <option value="overrides">Overrides (Replacement)</option>
            <option value="supports">Supports (Enhancement)</option>
          </select>
        </div>

        {/* Pack Search */}
        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Search Packs</label>
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                searchPacks(e.target.value);
              }}
              onFocus={() => setShowPicker(true)}
              placeholder="Type to search for packs..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            {isSearching && (
              <div className="absolute right-3 top-2.5 text-xs text-gray-400">Searching...</div>
            )}
          </div>

          {/* Search Results Dropdown */}
          {showPicker && searchResults.length > 0 && (
            <div className="mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-48 overflow-y-auto">
              {searchResults.map((pack) => (
                <div
                  key={pack.pack_id}
                  onClick={() => addRelationship(pack)}
                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-0"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-semibold text-gray-900">
                        {txt(pack.name, 'en', 'Unnamed Pack')}
                      </p>
                      <p className="text-[10px] text-gray-500">
                        {pack.pack_type} • {pack.metadata?.status || 'draft'}
                      </p>
                    </div>
                    <button className="text-xs text-blue-600 font-semibold">+ Add</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Existing Relationships - ChipTag Display */}
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-gray-700 mb-2">Linked Packs</label>
          {formData.relationships?.linked_packs?.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {formData.relationships.linked_packs.map((rel) => (
                <ChipTag
                  key={rel.pack_id}
                  label={`${rel.pack_name?.en || rel.pack_name || 'Unknown'} (${rel.relationship_type})`}
                  size="sm"
                  icon={getPackTypeIcon(rel.pack_type)}
                  onDelete={() => removeRelationship(rel.pack_id)}
                />
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-500 italic">No relationships added yet</p>
          )}
        </div>

        {/* Values & Compliance Pack References */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <label className="block text-xs font-semibold text-gray-700 mb-2">Special References</label>
          
          <div className="mb-3">
            <label className="block text-[10px] text-gray-600 mb-1">Values Pack ID (Optional)</label>
            <input
              type="text"
              value={formData.relationships?.values_pack_id || ''}
              onChange={(e) => updateFormData('relationships.values_pack_id', e.target.value)}
              placeholder="Link to a values pack for context"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-[10px] text-gray-600 mb-1">Compliance Pack ID (Optional)</label>
            <input
              type="text"
              value={formData.relationships?.compliance_pack_id || ''}
              onChange={(e) => updateFormData('relationships.compliance_pack_id', e.target.value)}
              placeholder="Link to a compliance pack for validation"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-[10px] text-gray-500">
            💡 Relationships help AI understand pack dependencies and ensure consistent knowledge delivery.
          </p>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STEP 4: COMPLIANCE & AI SETTINGS
// ============================================================================
const SettingsStep = ({ formData, updateFormData }) => {
  return (
    <div className="space-y-4">
      {/* Compliance Profile */}
      <div className="bg-white rounded-xl shadow-sm p-4" style={{ borderRadius: EMZI_RADIUS.md }}>
        <h3 className="text-base font-bold text-gray-900 mb-3">Compliance Profile</h3>

        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Compliance Level</label>
          <select
            value={formData.compliance_profile.level}
            onChange={(e) => updateFormData('compliance_profile.level', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="standard">Standard</option>
            <option value="strict">Strict</option>
            <option value="critical">Critical (Compliance Pack)</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Restricted Phrases (one per line)
          </label>
          <textarea
            value={formData.compliance_profile.restricted_phrases?.join('\n') || ''}
            onChange={(e) =>
              updateFormData(
                'compliance_profile.restricted_phrases',
                e.target.value.split('\n').filter((p) => p.trim())
              )
            }
            placeholder="guaranteed results&#10;100% cure&#10;miracle product"
            rows={4}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* AI Context Settings */}
      <div className="bg-white rounded-xl shadow-sm p-4" style={{ borderRadius: EMZI_RADIUS.md }}>
        <h3 className="text-base font-bold text-gray-900 mb-3">AI Context Settings</h3>

        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">Tone Influence</label>
          <select
            value={formData.ai_context.tone_influence}
            onChange={(e) => updateFormData('ai_context.tone_influence', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="neutral">Neutral</option>
            <option value="professional">Professional</option>
            <option value="friendly">Friendly</option>
            <option value="urgent">Urgent</option>
            <option value="empathetic">Empathetic</option>
          </select>
        </div>

        <div className="mb-4">
          <label className="block text-xs font-semibold text-gray-700 mb-1">
            Context Weight (1-10)
          </label>
          <input
            type="range"
            min="1"
            max="10"
            value={formData.ai_context.context_weight}
            onChange={(e) => updateFormData('ai_context.context_weight', parseInt(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-[10px] text-gray-500">
            <span>Low Priority</span>
            <span className="font-bold text-blue-600">{formData.ai_context.context_weight}</span>
            <span>High Priority</span>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="bg-white rounded-xl shadow-sm p-4" style={{ borderRadius: EMZI_RADIUS.md }}>
        <h3 className="text-base font-bold text-gray-900 mb-3">Pack Status</h3>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Publication Status</label>
          <select
            value={formData.status}
            onChange={(e) => updateFormData('status', e.target.value)}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="draft">Draft (Not visible to users)</option>
            <option value="published">Published (Active)</option>
          </select>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// STEP 5: REVIEW
// ============================================================================
const ReviewStep = ({ formData }) => {
  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl shadow-sm p-4" style={{ borderRadius: EMZI_RADIUS.md }}>
        <h3 className="text-base font-bold text-gray-900 mb-3">Review & Confirm</h3>
        <p className="text-xs text-gray-600 mb-4">
          Please review your pack details before submitting.
        </p>

        {/* Basic Info Summary */}
        <div className="mb-4 p-3 bg-gray-50 rounded-lg">
          <p className="text-xs font-semibold text-gray-700 mb-2">Basic Information</p>
          <div className="space-y-1 text-xs text-gray-600">
            <div>
              <span className="font-medium">Type:</span> {formData.pack_type}
            </div>
            <div>
              <span className="font-medium">Name (EN):</span> {formData.name.en || 'N/A'}
            </div>
            <div>
              <span className="font-medium">Name (MS):</span> {formData.name.ms || 'N/A'}
            </div>
            <div>
              <span className="font-medium">Visibility:</span> {formData.visibility}
            </div>
            <div>
              <span className="font-medium">Status:</span> {formData.status}
            </div>
            {formData.tags.length > 0 && (
              <div>
                <span className="font-medium">Tags:</span> {formData.tags.join(', ')}
              </div>
            )}
          </div>
        </div>

        {/* Content Summary */}
        <div className="mb-4 p-3 bg-blue-50 rounded-lg">
          <p className="text-xs font-semibold text-blue-700 mb-2">Content Fields</p>
          <div className="text-xs text-blue-600">
            {Object.keys(formData.content).length} fields configured
          </div>
        </div>

        {/* Compliance Summary */}
        <div className="mb-4 p-3 bg-green-50 rounded-lg">
          <p className="text-xs font-semibold text-green-700 mb-2">Compliance</p>
          <div className="space-y-1 text-xs text-green-600">
            <div>
              <span className="font-medium">Level:</span> {formData.compliance_profile.level}
            </div>
            <div>
              <span className="font-medium">Restricted Phrases:</span>{' '}
              {formData.compliance_profile.restricted_phrases?.length || 0}
            </div>
          </div>
        </div>

        {/* AI Context Summary */}
        <div className="p-3 bg-purple-50 rounded-lg">
          <p className="text-xs font-semibold text-purple-700 mb-2">AI Context</p>
          <div className="space-y-1 text-xs text-purple-600">
            <div>
              <span className="font-medium">Tone:</span> {formData.ai_context.tone_influence}
            </div>
            <div>
              <span className="font-medium">Weight:</span> {formData.ai_context.context_weight}/10
            </div>
          </div>
        </div>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
        <p className="text-xs font-semibold text-yellow-700 mb-1">⚠️ Ready to Submit?</p>
        <p className="text-xs text-yellow-600">
          Click "Create Pack" below to save this Knowledge Pack. It will be immediately available based on
          the status and visibility settings you configured.
        </p>
      </div>
    </div>
  );
};

export default KnowledgePackWizardV3;
